<script>
import { mapState } from 'vuex';
import { preferredName } from '@/lib/agreement/filters';
import holidayScheduleJurisdiction from '@/lib/holidayScheduleProvince';
import HolidayBuilder from '@/holiday-schedule/HolidayBuilder.vue';

const PRONOUN_MAP = {
	male: 'he/him',
	female: 'she/her',
	neutral: 'they/them',
};

export default {
	name: 'HolidaySchedulePanel',
	components: { HolidayBuilder },
	props: {
		tabChangeToBuilder: {
			type: Function,
			default: null,
		},
		agreementData: {
			type: Object,
			default: null,
		},
	},
	computed: {
		...mapState(['agreement', 'user']),
		sourceAgreement() {
			return this.agreementData || this.agreement || null;
		},
		partyAName() {
			return preferredName(this.sourceAgreement?.partner1) || 'Party A';
		},
		partyBName() {
			return preferredName(this.sourceAgreement?.partner2) || 'Party B';
		},
		partyAPronouns() {
			return PRONOUN_MAP[this.sourceAgreement?.partner1?.pronouns] || '';
		},
		partyBPronouns() {
			return PRONOUN_MAP[this.sourceAgreement?.partner2?.pronouns] || '';
		},
		jurisdiction() {
			return holidayScheduleJurisdiction(this.sourceAgreement);
		},
		caseId() {
			return (
				this.$route?.params?.id ||
				this.agreement?.id ||
				this.sourceAgreement?.id ||
				null
			);
		},
		children() {
			return (this.sourceAgreement?.children || [])
				.map((child) => {
					const name = preferredName(child);
					return name ? { name } : null;
				})
				.filter(Boolean);
		},
		currentUserName() {
			return (
				this.user?.displayName ||
				this.user?.email ||
				''
			);
		},
	},
	mounted() {
		if (!this.caseId && typeof this.tabChangeToBuilder === 'function') {
			this.tabChangeToBuilder(0);
		}
	},
};
</script>

<template>
	<div v-if="caseId" class="holiday-schedule-panel">
		<HolidayBuilder
			:party-a-name="partyAName"
			:party-b-name="partyBName"
			:party-a-pronouns="partyAPronouns"
			:party-b-pronouns="partyBPronouns"
			:jurisdiction="jurisdiction"
			:case-id="caseId"
			:children="children"
			:current-user-name="currentUserName"
			role="primary"
		/>
	</div>
</template>

<style scoped>
.holiday-schedule-panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: calc(100vh - 180px);
  max-height: calc(100vh - 180px);
}
@media (max-width: 1000px) {
  .holiday-schedule-panel {
    height: auto;
    max-height: none;
  }
}
</style>

<style>
@media print {
	body.holiday-schedule-printing .print-hide,
	body.holiday-schedule-printing .agreement-navigation,
	body.holiday-schedule-printing .active-user,
	body.holiday-schedule-printing .nav-bar,
	body.holiday-schedule-printing .left-col,
	body.holiday-schedule-printing #intercom-container,
	body.holiday-schedule-printing .intercom-lightweight-app,
	body.holiday-schedule-printing .intercom-app,
	body.holiday-schedule-printing [class*="intercom"] {
		display: none !important;
	}
	/* Bootstrap row/col negative margins shift print content under the page edge. */
	body.holiday-schedule-printing .content-row {
		margin: 0 !important;
		height: auto !important;
		flex-wrap: wrap !important;
	}
	body.holiday-schedule-printing .right {
		padding: 0 !important;
		margin: 0 !important;
		flex: 0 0 100% !important;
		max-width: 100% !important;
		width: 100% !important;
	}
	body.holiday-schedule-printing .tab-content,
	body.holiday-schedule-printing .tab-content > div,
	body.holiday-schedule-printing .custom-scroll,
	body.holiday-schedule-printing .holiday-schedule-panel,
	body.holiday-schedule-printing .holiday-builder {
		overflow: visible !important;
		max-height: none !important;
		height: auto !important;
		padding: 0 !important;
		margin: 0 !important;
		width: 100% !important;
		position: static !important;
	}
}
</style>
